import React, { FC, useEffect, useRef, useState } from 'react'

import { EuiTitle, EuiSpacer, EuiButton, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { Persona } from 'api/interfaces/Persona'

import SparklesIcon from '../../SparklesIcon'
import PersonaCard from './PersonaCard'

export interface PersonaListProps {
  personas: Persona[]
  onEditPersonaClick?: (persona: Persona) => void
  onAIEditPersonaClick?: (persona: Persona) => void
  onAIAddPersonaClick?: () => void
  onReorderPersonasClick?: () => void
  onDeletePersonaClick?: (persona: Persona) => Promise<void>
  onDeletePersonaLoading?: boolean
  showActions?: boolean
}

const PersonaList: FC<PersonaListProps> = ({ personas, onEditPersonaClick, onAIEditPersonaClick, onAIAddPersonaClick, onReorderPersonasClick, onDeletePersonaClick, onDeletePersonaLoading = false, showActions = true }) => {
  const sortedPersonas = [...personas].sort((a, b) => a.rank - b.rank)
  const [expandedPersonas, setExpandedPersonas] = useState<Set<number>>(new Set(sortedPersonas.map(p => p.rank)))
  const previousPersonaIds = useRef<Set<string>>(new Set(personas.map(p => p.id)))

  useEffect(() => {
    const currentIds = new Set(personas.map(p => p.id))
    const newPersonas = personas.filter(p => !previousPersonaIds.current.has(p.id))
    previousPersonaIds.current = currentIds

    if (newPersonas.length > 0) {
      setExpandedPersonas(prev => {
        const updated = new Set(prev)
        newPersonas.forEach(p => updated.add(p.rank))
        return updated
      })
    }
  }, [personas])

  const togglePersona = (rank: number) => {
    const newExpanded = new Set(expandedPersonas)
    if (newExpanded.has(rank)) {
      newExpanded.delete(rank)
    } else {
      newExpanded.add(rank)
    }
    setExpandedPersonas(newExpanded)
  }

  return (
    <div>
      {showActions && (
        <>
          <EuiFlexGroup alignItems='center' justifyContent='spaceBetween'>
            <EuiFlexItem grow={false} />
            <EuiFlexItem grow={false}>
              <EuiFlexGroup gutterSize='s' responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiButton size='s' onClick={onAIAddPersonaClick}>
                    <EuiFlexGroup gutterSize='s' alignItems='center' responsive={false}>
                      <EuiFlexItem grow={false}>Add Persona</EuiFlexItem>
                      <EuiFlexItem grow={false}><SparklesIcon /></EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiButton>
                </EuiFlexItem>
                {personas.length > 1 && (
                  <EuiFlexItem grow={false}>
                    <EuiButton size='s' onClick={onReorderPersonasClick}>
                      Change Order
                    </EuiButton>
                  </EuiFlexItem>
                )}
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiSpacer size='m' />
        </>
      )}

      {sortedPersonas.map((persona, index) => (
        <div key={persona.name}>
          <PersonaCard persona={persona} isExpanded={expandedPersonas.has(persona.rank)} onToggle={() => togglePersona(persona.rank)} onEditClick={showActions ? () => onEditPersonaClick?.(persona) : undefined} onAIEditClick={showActions ? () => onAIEditPersonaClick?.(persona) : undefined} onDeleteClick={showActions && onDeletePersonaClick ? () => onDeletePersonaClick(persona) : undefined} onDeleteLoading={onDeletePersonaLoading} showActions={showActions} />
          {index < sortedPersonas.length - 1 && <EuiSpacer size='m' />}
        </div>
      ))}
    </div>
  )
}

export default PersonaList
